import React, { useCallback, useState } from 'react';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import { useMultiFactorAuthenticationContext } from './MultiFactorAuthenticationContext';
import {
  EnforcementStatus,
  MultiFactorAuthenticationType,
} from '../../../Infrastructure/API/MultiFactorAuthentication/list';
import Email from './Email';
import Sms from './Sms';
import Totp from './Totp';
import TotpActivation from './TotpActivation';
import { FormRenderProps } from 'react-final-form';
import AlternativeMethods from './AlternativeMethods';
import { useCountdownTimer } from './useCountdownTimer';
import useFeatureFlag from '../../../Infrastructure/FeatureFlags/useFeatureFlag';
import FeatureFlags from '../../../Infrastructure/FeatureFlags/FeatureFlags';

interface MultiFactorAuthenticationProps {
  formRenderProps: FormRenderProps;
  onGoBack: () => void;
  isRedirecting: boolean;
}

const MultiFactorAuthentication = ({
  formRenderProps,
  onGoBack: handleGoBackToLoginClick,
  isRedirecting,
}: MultiFactorAuthenticationProps) => {
  const hasMfaActivationDuringLoginEnabled = useFeatureFlag(FeatureFlags.EnableMfaActivationDuringLogin);
  const { authentications, defaultAuthentication, enforcementStatus } = useMultiFactorAuthenticationContext();
  const emailCountdownTimer = useCountdownTimer();
  const smsCountdownTimer = useCountdownTimer();

  const [activeAuthentication, setActiveAuthentication] = useState<MultiFactorAuthenticationType | null>();

  const handleChooseAnotherMethodClick = useCallback(() => {
    setActiveAuthentication(null);
  }, []);

  const isActive = (authenticationType: MultiFactorAuthenticationType) => {
    if (isUndefined(activeAuthentication)) {
      return defaultAuthentication?.provider === authenticationType;
    }
    return activeAuthentication === authenticationType;
  };

  const shouldEnforce =
    enforcementStatus === EnforcementStatus.Enforced || enforcementStatus === EnforcementStatus.PendingEnforcement;
  const isActivationRequired = authentications.length === 0 && shouldEnforce;

  if (isActivationRequired && hasMfaActivationDuringLoginEnabled) {
    return <TotpActivation onGoBackToLoginClick={handleGoBackToLoginClick} />;
  }

  const isListActive =
    isNull(activeAuthentication) || (isUndefined(activeAuthentication) && isUndefined(defaultAuthentication));

  return (
    <>
      {isActive(MultiFactorAuthenticationType.TOTP) && (
        <Totp
          formRenderProps={formRenderProps}
          onGoBackToLoginClick={handleGoBackToLoginClick}
          onChooseAnotherMethodClick={handleChooseAnotherMethodClick}
          isRedirecting={isRedirecting}
        />
      )}
      {isActive(MultiFactorAuthenticationType.SMS) && (
        <Sms
          formRenderProps={formRenderProps}
          countdownTimer={smsCountdownTimer}
          onGoBackToLoginClick={handleGoBackToLoginClick}
          onChooseAnotherMethodClick={handleChooseAnotherMethodClick}
          isRedirecting={isRedirecting}
        />
      )}
      {isActive(MultiFactorAuthenticationType.Email) && (
        <Email
          formRenderProps={formRenderProps}
          countdownTimer={emailCountdownTimer}
          onGoBackToLoginClick={handleGoBackToLoginClick}
          onChooseAnotherMethodClick={handleChooseAnotherMethodClick}
          isRedirecting={isRedirecting}
        />
      )}
      {isListActive && (
        <AlternativeMethods onGoBackToLoginClick={handleGoBackToLoginClick} onSubmit={setActiveAuthentication} />
      )}
    </>
  );
};

export default MultiFactorAuthentication;
